/**
 * Bot control action handlers for BitBot Dashboard.
 * Encapsulates all bot start/stop/pause, trade actions, export, and share logic.
 */
import axios from 'axios';
import { toast } from 'sonner';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

/**
 * Create bot control handlers. Receives Dashboard state/setters and returns action functions.
 */
export function createBotControls({
  user,
  tradingMode,
  botStatus,
  setBotStatus,
  setShowStopConfirm,
  setTradingMode,
  setDumpConfirm,
  setBleedPairs,
  setBleedLoading,
  setTrades,
  setPriceHistory,
  countdownMusicRef,
  musicPlayingRef,
  fetchBotStatus,
  fetchTrades,
  playAlertSound,
}) {
  const handleStartBot = async () => {
    if (!user) {
      toast.error('Not authenticated', { description: 'Please log in to control the bot' });
      return;
    }

    try {
      await axios.post(`${API_V2}/user/bot/start`, {}, { withCredentials: true });
      setBotStatus('running');
      toast.success('Trading bot started', { description: `Mode: ${tradingMode.toUpperCase()}` });
    } catch (error) {
      toast.error('Failed to start bot', { description: error.response?.data?.detail });
    }
  };

  const handleStopBot = async () => {
    if (!user) {
      toast.error('Not authenticated', { description: 'Please log in to control the bot' });
      return;
    }
    setShowStopConfirm(true);
  };

  const confirmStopBot = async () => {
    setShowStopConfirm(false);
    try {
      await axios.post(`${API_V2}/user/bot/stop`, {}, { withCredentials: true });
      setBotStatus('stopped');
      toast.info('Trading bot stopped');
    } catch (error) {
      toast.error('Failed to stop bot', { description: error.response?.data?.detail });
    }
  };

  const handlePauseBot = async () => {
    if (!user) {
      toast.error('Not authenticated', { description: 'Please log in to control the bot' });
      return;
    }
    try {
      await axios.post(`${API_V2}/user/bot/pause`, {}, { withCredentials: true });
      setBotStatus('paused');
      toast.info('Trading bot paused');
    } catch (error) {
      toast.error('Failed to pause bot', { description: error.response?.data?.detail });
    }
  };

  const handleModeChange = async (isLive) => {
    if (botStatus === 'running') {
      toast.error('Stop the bot first before changing mode', {
        description: 'Click STOP button, then toggle the mode switch'
      });
      return;
    }
    try {
      const newMode = isLive ? 'live' : 'simulation';
      await axios.put(`${API_V2}/user/bot/mode`, { mode: newMode }, { withCredentials: true });
      setTradingMode(newMode);
      toast.success(`Mode changed to ${newMode.toUpperCase()}`, {
        description: newMode === 'live' ? 'Using real Coinbase prices' : 'Using simulated prices'
      });
    } catch (error) {
      toast.error('Failed to change mode', { description: error.response?.data?.detail });
    }
  };

  const handleResetPortfolio = async () => {
    if (!user) {
      toast.error('Not authenticated', { description: 'Please log in to reset portfolio' });
      return;
    }
    if (botStatus === 'running') {
      toast.error('Stop the bot before resetting');
      return;
    }
    try {
      await axios.post(`${API_V2}/user/portfolio/reset`, {}, { withCredentials: true });
      fetchBotStatus();
      fetchTrades();
      setPriceHistory([]);
      toast.success('Portfolio reset to initial state');
    } catch (error) {
      toast.error('Failed to reset portfolio', { description: error.response?.data?.detail });
    }
  };

  const handleSellHalf = async () => {
    if (!user) {
      toast.error('Not authenticated');
      return;
    }
    try {
      const response = await axios.post(`${API_V2}/user/bot/sell-half`, {}, { withCredentials: true });
      const data = response.data;
      playAlertSound('sell');
      toast.success(`Sold 50% of holdings`, {
        description: `$${data.total_usd_received?.toFixed(2) || data.usd_received?.toFixed(2)} received | P/L: $${data.total_profit_loss?.toFixed(4) || data.profit_loss?.toFixed(4)}`
      });
      fetchBotStatus();
      fetchTrades();
    } catch (error) {
      toast.error('Failed to sell', { description: error.response?.data?.detail });
    }
  };

  const handleDumpCoin = async (pair) => {
    if (!user) {
      toast.error('Not authenticated');
      setDumpConfirm({ open: false, pair: null, coinName: null });
      return;
    }
    try {
      const response = await axios.post(`${API_V2}/user/bot/dump-coin`, { pair }, { withCredentials: true });
      const data = response.data;
      const coinName = pair.split('-')[0];
      playAlertSound('sell');
      toast.success(`DUMPED all ${coinName}`, {
        description: `Sold ${data.amount_sold?.toFixed(6)} ${coinName} for $${data.usd_received?.toFixed(2)} | P/L: $${data.profit_loss?.toFixed(4)}`
      });
      fetchBotStatus();
      fetchTrades();
    } catch (error) {
      toast.error('Failed to dump coin', { description: error.response?.data?.detail || error.message });
    } finally {
      setDumpConfirm({ open: false, pair: null, coinName: null });
    }
  };

  const handleToggleBleed = async (pair) => {
    if (!user) {
      toast.error('Not authenticated');
      return;
    }
    setBleedLoading(pair);
    try {
      const response = await axios.post(`${API_V2}/user/bot/toggle-bleed`, { pair }, { withCredentials: true });
      const data = response.data;
      if (data.success) {
        setBleedPairs(data.bleed_pairs);
        const coinName = pair.split('-')[0];
        if (data.bleed_enabled) {
          toast.success(`${coinName} BLEED mode ON`, { description: 'Bot will only SELL this coin, no more buys until disabled.' });
        } else {
          toast.success(`${coinName} BLEED mode OFF`, { description: 'Normal trading resumed for this coin.' });
        }
      }
    } catch (error) {
      toast.error('Failed to toggle bleed mode', { description: error.response?.data?.detail || error.message });
    } finally {
      setBleedLoading(null);
    }
  };

  const handleClearTradeHistory = async () => {
    if (!user) {
      toast.error('Not authenticated');
      return;
    }
    try {
      if (botStatus === 'running') {
        await axios.post(`${API_V2}/user/bot/stop`, {}, { withCredentials: true });
        setBotStatus('stopped');
      }
      await axios.post(`${API_V2}/user/portfolio/reset`, {}, { withCredentials: true });
      setTrades([]);
      setPriceHistory([]);
      fetchBotStatus();
      toast.success('Trade history cleared');
    } catch (error) {
      toast.error('Failed to clear history', { description: error.response?.data?.detail });
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API_V2}/user/trades/export?format=csv&limit=1000`, {
        responseType: 'blob', withCredentials: true
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trade_history_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Trade history exported as CSV');
    } catch (error) {
      toast.error('Failed to export trades', { description: error.message });
    }
  };

  const handleExportJSON = async () => {
    try {
      const response = await axios.get(`${API_V2}/user/trades/export?format=json&limit=1000`, { withCredentials: true });
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trade_history_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Trade history exported as JSON');
    } catch (error) {
      toast.error('Failed to export trades', { description: error.message });
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: 'BitBot V3.9.32 [LIVE] - Automated Bitcoin Trading',
      text: 'Check out BitBot V3.9.32 [LIVE] - Automated cryptocurrency trading with RSI-based strategies!',
      url: window.location.origin
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
        return;
      } catch (err) {
        if (err.name !== 'AbortError') console.log('Web Share failed, falling back to menu');
      }
    }

    const shareUrl = encodeURIComponent(window.location.origin);
    const shareText = encodeURIComponent(shareData.text);
    const shareOptions = [
      { name: 'Facebook', url: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}` },
    ];

    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast.success('Link copied to clipboard!', {
        description: 'Share on your favorite platform',
        action: { label: 'Facebook', onClick: () => window.open(shareOptions[0].url, '_blank', 'width=600,height=400') }
      });
    } catch {
      window.open(shareOptions[0].url, '_blank', 'width=600,height=400');
    }
  };

  return {
    handleStartBot,
    handleStopBot,
    confirmStopBot,
    handlePauseBot,
    handleModeChange,
    handleResetPortfolio,
    handleSellHalf,
    handleDumpCoin,
    handleToggleBleed,
    handleClearTradeHistory,
    handleExportCSV,
    handleExportJSON,
    handleShare,
  };
}
